/* Mybe.GG — V19: full-width mobile variant shelf, one open product at a time.
   Keeps native inline <details> on desktop and MybeCardQuickQty for cart updates. */
(() => {
  if (window.MybeInlineLoadoutInstalled) return;
  window.MybeInlineLoadoutInstalled = true;

  const media = window.matchMedia('(max-width: 749px)');
  const entries = new WeakMap();
  let active = null;
  let scheduled = false;

  function initialize(deck) {
    const slot = deck.querySelector('[data-mybe-loadout-slot]');
    const template = deck.querySelector('template.mybe-loadout-inline__template');
    if (!slot || !template || slot.dataset.loaded) return;
    slot.append(template.content.cloneNode(true));
    slot.dataset.loaded = 'true';
    const handler = window.MybeCardQuickQty;
    const cart = handler?.getCart?.();
    if (cart?.items) handler.refresh(cart);
    else if (window.MybeInitialCart?.load) {
      window.MybeInitialCart.load().then(cart => {
        if (cart?.items) window.MybeCardQuickQty?.refresh?.(cart);
      }).catch(() => {});
    }
  }

  function createShelf(anchor) {
    const sibling = anchor.nextElementSibling;
    if (sibling?.classList.contains('mybe-v19-shelf')) return sibling;
    const shelf = document.createElement('div');
    shelf.className = 'mybe-mobile-variant-shelf mybe-v19-shelf';
    shelf.hidden = true;
    anchor.insertAdjacentElement('afterend', shelf);
    return shelf;
  }

  function close(entry = active) {
    if (!entry) return;
    if (entry.launcher.isConnected) {
      entry.launcher.after(entry.deck);
    }
    entry.deck.open = false;
    entry.launcher.setAttribute('aria-expanded', 'false');
    entry.launcher.classList.remove('is-open');
    if (entry.panel) {
      const shelf = entry.panel.parentElement;
      entry.panel.remove();
      if (shelf?.classList.contains('mybe-v19-shelf')) shelf.hidden = true;
    }
    entry.panel = null;
    if (active === entry) active = null;
  }

  function open(entry) {
    if (!media.matches) {
      entry.deck.open = true;
      initialize(entry.deck);
      return;
    }
    if (active === entry && entry.panel?.isConnected) return;
    if (active && active !== entry) close(active);

    const anchor = entry.card.closest('slider-element') ||
      entry.card.closest('.product-grid') || entry.card.closest('.card-grid');
    if (!anchor || !anchor.isConnected) return;
    const shelf = createShelf(anchor);

    const panel = document.createElement('section');
    panel.className = 'mybe-mobile-variant-panel mybe-v19-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', `Variants for ${entry.title}`);

    const header = document.createElement('div');
    header.className = 'mybe-v19-panel__header';
    const title = document.createElement('div');
    title.className = 'mybe-v19-panel__title';
    title.textContent = entry.title;
    const closeButton = document.createElement('button');
    closeButton.className = 'mybe-v19-panel__close';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', `Close variants for ${entry.title}`);
    closeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    closeButton.addEventListener('click', () => {
      close(entry);
      if (entry.launcher.isConnected) entry.launcher.focus({preventScroll: true});
    });
    header.append(title, closeButton);
    panel.append(header);
    panel.append(entry.deck);
    shelf.append(panel);
    shelf.hidden = false;

    entry.panel = panel;
    active = entry;
    entry.deck.open = true;
    entry.launcher.classList.add('is-open');
    entry.launcher.setAttribute('aria-expanded', 'true');
    initialize(entry.deck);

    // Move only enough to reveal the newly opened shelf, avoiding a forced page-top jump.
    requestAnimationFrame(() => {
      if (!panel.isConnected || active !== entry) return;
      const rect = panel.getBoundingClientRect();
      if (rect.top >= window.innerHeight - 95 || rect.bottom < 0) {
        panel.scrollIntoView({block:'nearest',behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }
    });
  }

  function addLauncher(deck) {
    if (entries.has(deck)) return;
    const card = deck.closest('.product-card');
    if (!card) return;
    const title = card.querySelector('.product-card__title')?.textContent?.trim() || 'Product';
    const launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'mybe-mobile-variant-launch mybe-v19-launcher';
    launcher.setAttribute('aria-label', `Show versions for ${title}`);
    launcher.setAttribute('aria-expanded','false');
    launcher.innerHTML = '<span class="mybe-mobile-variant-launch__arrow" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6"/></svg></span>';
    deck.before(launcher);
    deck.classList.add('mybe-mobile-source','mybe-v19-source');
    const entry = {deck,card,launcher,title,panel:null};
    entries.set(deck,entry);
    launcher.addEventListener('click',event => {
      event.preventDefault();
      event.stopPropagation();
      if (active === entry && entry.panel) close(entry);
      else open(entry);
    });
  }

  function scan() {
    document.querySelectorAll('.product-card details[data-mybe-inline-loadout]').forEach(addLauncher);
    // Shopify's tab/section replacement may detach the previously opened product.
    if (active && (!active.card.isConnected || !active.launcher.isConnected)) {
      close(active);
    }
  }

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {scheduled = false; scan();});
  }

  // On wide screens the original compact card selector remains unchanged.
  media.addEventListener?.('change', () => {
    if (!media.matches && active) close(active);
    scheduleScan();
  });

  document.addEventListener('toggle', event => {
    const deck = event.target;
    if (!deck.matches?.('details[data-mybe-inline-loadout]') || !deck.open) return;
    initialize(deck);
    // Desktop: keep native details behaviour. Mobile: the launcher owns the open state.
  }, true);

  document.addEventListener('click', event => {
    const option = event.target.closest?.('[data-mybe-variant-option]');
    if (!option || option.disabled) return;
    const deck = option.closest('details[data-mybe-inline-loadout]');
    if (!deck) return;
    event.preventDefault();
    const index = option.dataset.mybeVariantIndex;
    deck.querySelectorAll('[data-mybe-variant-option]').forEach(node => {
      const chosen = node.dataset.mybeVariantIndex === index;
      node.classList.toggle('is-selected', chosen);
      node.setAttribute('aria-pressed', chosen ? 'true':'false');
    });
    deck.querySelectorAll('[data-mybe-variant-action]').forEach(node => {
      node.hidden = node.dataset.mybeVariantIndex !== index;
    });
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && active?.panel) {
      const launcher = active.launcher;
      close(active);
      if (launcher.isConnected) launcher.focus({preventScroll:true});
    }
  });

  document.addEventListener('shopify:section:load', scheduleScan);
  document.addEventListener('shopify:section:unload', () => {
    if (active) close(active);
    scheduleScan();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',scan,{once:true});
  else scan();

  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches?.('.product-card') ||
        node.querySelector?.('.product-card details[data-mybe-inline-loadout]'))))) scheduleScan();
  });
  observer.observe(document.documentElement,{childList:true,subtree:true});
})();
